import React, { useState, useRef, useMemo, useImperativeHandle, forwardRef } from 'react'
import { Button, IconButton, Snackbar, CircularProgress, Box, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import Swal from 'sweetalert2'
import { closeAllPingSnackbars } from './PingResultSnackbarController'

// Interpreta la línea completa (viene a trozos del ping)
//Devuelve null si no es una linea de resultado de ping
const parsePingLine = (line) => {
  const PING_HEADER_REGEX = /^Pinging\s/i
  const PING_TIME_REGEX = /time[=<]\s*(\d+)\s*ms/i
  const PING_TIMEOUT_REGEX = /timed out|unreachable/i
  const trimmed = line.trim()
  if (!trimmed) return null
  if (PING_HEADER_REGEX.test(trimmed)) return null // lo de pinging to x with y bytes

  const match = trimmed.match(PING_TIME_REGEX)
  if (match) {
    return { success: true, time: Number(match[1]), text: trimmed }
  }
  if (PING_TIMEOUT_REGEX.test(trimmed)) {
    return { success: false, time: null, text: trimmed }
  }
  // Línea desconocida, se ignora
  return null
}

export const PingResultSnackbar = forwardRef(({ name, ip, onFinalize, index: initialIndex = 0 }, ref) => {
  const [open, setOpen] = useState(true)
  const [message, setMessage] = useState(`${name} (${ip}) - PING en curso...`)
  const [results, setResults] = useState([])
  const [index, setIndex] = useState(initialIndex)
  const msgBuffer = useRef('') // los mensajes incompletos asiq los acumulo en un buffer hasta que llega el fin de linea 

  // Compartir metodos con el padre, los cuales estan "conectados" mediante ref
  useImperativeHandle(ref, () => ({
    updateMessage: (chunk) => {
      //      console.log('chunk:', JSON.stringify(chunk))
      msgBuffer.current += chunk
      const lines = msgBuffer.current.split(/\r\n|\n/)
      msgBuffer.current = lines.pop()

      const parsedLine = lines.map(parsePingLine).filter(Boolean)
      if (parsedLine.length > 0) {
        setResults(prev => [...prev, ...parsedLine])
        setMessage(`${name} (${ip}) - ${parsedLine[parsedLine.length - 1].text}`)
      }
    },
    updateIndex: (newIndex) => {
      setIndex(newIndex)
    },
    close: () => {
      setOpen(false)
    }
  }))

  const pingStats = useMemo(() => {
    const sent = results.length
    const times = results.filter(r => r.success).map(r => r.time)
    const received = times.length
    const lost = sent - received
    const lossPercent = sent > 0 ? Math.round((lost / sent) * 100) : 0
    const min = times.length ? Math.min(...times) : null
    const max = times.length ? Math.max(...times) : null
    const avg = times.length ? Math.round(times.reduce((a, b) => a + b, 0) / times.length) : null
    return { sent, received, lost, lossPercent, min, max, avg }
  }, [results])

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return
    setOpen(false)
    onFinalize?.()
  }

  const handleCloseAllPings = () => {
    closeAllPingSnackbars()
  }

  const handleConfirmClose = async () => {
    const result = await Swal.fire({
      title: '¿Finalizar PINGs?',
      text: 'Se cancelarán todos los PINGs en curso.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, finalizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33',
    })

    if (result.isConfirmed) {
      closeAllPingSnackbars()
    }
  }

  const action = (
    <React.Fragment>
      <Button color="blue" size="small" onClick={handleCloseAllPings}>
        Finalizar PING
      </Button>
      <IconButton size="small" color="inherit" onClick={() => handleClose(null, 'closeButton')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  )

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      message={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} color="inherit" />
            {message}
          </Box>
          {pingStats.sent > 0 && (
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Enviados = {pingStats.sent}, Recibidos = {pingStats.received}, Perdidos = {pingStats.lost}
            </Typography>
          )}
        </Box>
      }
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      action={action}
      sx={{
        '&.MuiSnackbar-root': {
          top: `${25 + index * 130}px`,
          bottom: 'unset',
          left: '50%',
          right: 'unset',
          transform: 'translateX(-50%)',
          width: '600px',
        },
        '& .MuiSnackbarContent-root': {
          width: '100%',
        }
      }}
    />
  )
})